import React, { useState, useEffect, useRef } from 'react';
import { getShlokaOfTheDay } from '../services/mockService';
import { DailyShloka, Tab } from '../types';
import { BookOpen, X, Quote, Sparkles, ChevronRight, Volume2, Download, Share2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { ShareModal } from './ShareModal';
import { isNativeAlarmAvailable } from '../services/nativeAlarmBridge';

// Golok Logo URL - using local copy for better CORS handling
const GOLOK_LOGO_URL = '/golok-logo.png';

interface ShlokaCardProps {
  onNavigate?: (tab: Tab) => void;
  compact?: boolean;
}

export const ShlokaCard: React.FC<ShlokaCardProps> = ({ onNavigate, compact = false }) => {
  const [shloka, setShloka] = useState<DailyShloka | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShloka(getShlokaOfTheDay());
  }, []);

  const handleDownload = async () => {
    if (!downloadRef.current) return;
    
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(downloadRef.current, {
        backgroundColor: '#FDF5E6',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      
      const fileName = `suvichar-${new Date().toISOString().split('T')[0]}.png`;

      // Native Android: save via NativeAlarm bridge
      if (isNativeAlarmAvailable() && (window as any).NativeAlarm?.saveBase64Image) {
        const base64 = canvas.toDataURL('image/png');
        const result = JSON.parse((window as any).NativeAlarm.saveBase64Image(base64, fileName));
        if (result.success) {
          alert('चित्र Downloads में सेव हो गया!');
          return;
        }
      }

      // Fallback: browser download
      const link = document.createElement('a');
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!shloka) return null;

  if (compact) {
    return (
      <div 
        onClick={() => onNavigate?.(Tab.SHLOKA_OF_DAY)}
        className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-stone-800 dark:to-stone-900 p-4 rounded-2xl border border-amber-200/50 dark:border-stone-700 cursor-pointer hover:shadow-lg transition-all group"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-saffron to-amber-500 flex items-center justify-center shrink-0 shadow-md">
            {shloka.type === 'shloka' ? (
              <BookOpen size={18} className="text-white" />
            ) : (
              <Quote size={18} className="text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-400">
                {shloka.type === 'shloka' ? 'Shloka' : 'Quote'} of the Day
              </span>
              <Sparkles size={10} className="text-amber-500 animate-pulse" />
            </div>
            <p className="text-xs font-hindi font-medium text-gray-700 dark:text-stone-300 line-clamp-2 leading-relaxed">
              {shloka.sanskritText.split('\n')[0]}...
            </p>
            <p className="text-[10px] text-gray-500 dark:text-stone-500 mt-1 font-medium">
              — {shloka.source}
            </p>
          </div>
          <ChevronRight size={16} className="text-amber-500 group-hover:translate-x-1 transition-transform shrink-0" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-stone-900 dark:via-stone-800 dark:to-stone-900 rounded-2xl border border-amber-200/50 dark:border-stone-700 overflow-hidden shadow-xl">
      {/* Downloadable Content Area */}
      <div ref={downloadRef} className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        {/* Header with #FDF5E6 and Golok logo */}
        <div className="px-4 py-3 flex flex-col items-center border-b-2 border-saffron/30" style={{ backgroundColor: '#FDF5E6' }}>
          <img 
            src={GOLOK_LOGO_URL} 
            alt="Golok Logo" 
            className="h-12 w-auto mb-1 object-contain"
          />
          <div className="text-center">
            <h3 className="text-templeRed font-bold text-base">
              {shloka.type === 'shloka' ? 'श्लोक' : 'सुविचार'} of the Day
            </h3>
            <p className="text-templeRed/70 text-xs">{shloka.source}</p>
          </div>
        </div>

        {/* Content - More compact */}
        <div className="p-4 space-y-3">
          {/* Sanskrit Text */}
          <div className="bg-white dark:bg-stone-800 rounded-xl p-3 border border-amber-100 dark:border-stone-700 shadow-inner">
            <p className="font-hindi text-base md:text-lg text-center leading-relaxed text-templeRed dark:text-saffron whitespace-pre-line">
              {shloka.sanskritText}
            </p>
          </div>

          {/* Hindi Meaning */}
          <div className="bg-amber-50 dark:bg-stone-800/50 rounded-lg p-3 border border-amber-100 dark:border-stone-700">
            <p className="font-hindi text-xs text-gray-700 dark:text-stone-300 leading-relaxed">
              {shloka.hindiMeaning}
            </p>
          </div>

          {/* Tags */}
          {shloka.tags && shloka.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center">
              {shloka.tags.slice(0, 3).map(tag => (
                <span 
                  key={tag} 
                  className="px-2 py-0.5 bg-amber-100 dark:bg-stone-700 text-amber-700 dark:text-amber-400 text-[9px] font-bold uppercase tracking-wider rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Source Info */}
          <div className="text-center">
            <p className="text-[10px] text-gray-500 dark:text-stone-500">
              {shloka.author ? `— ${shloka.author}` : `— ${shloka.source}`}
            </p>
          </div>
        </div>
      </div>

      {/* Actions - Outside downloadable area */}
      <div className="px-4 pb-4 space-y-2">
        {/* English Meaning (Collapsible) */}
        {shloka.englishMeaning && (
          <div className="text-center">
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-xs font-bold text-templeRed dark:text-amber-400 hover:underline flex items-center gap-1 mx-auto"
            >
              {expanded ? 'Hide' : 'Show'} English Translation
              <ChevronRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
            {expanded && (
              <p className="text-xs text-gray-600 dark:text-stone-400 mt-1 italic leading-relaxed animate-fade-in">
                "{shloka.englishMeaning}"
              </p>
            )}
          </div>
        )}

        {/* Download & Share Buttons */}
        <div className="flex gap-2">
          <button 
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex-1 py-2.5 bg-white border-2 border-templeRed/30 text-templeRed font-bold rounded-xl shadow-md hover:bg-templeRed/5 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
          >
            <Download size={16} />
            {isDownloading ? 'Downloading...' : 'Download'}
          </button>
          <button 
            onClick={() => setShowShare(true)}
            data-testid="shloka-card-share-btn"
            className="px-4 py-2.5 bg-gradient-to-r from-saffron to-orange-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 text-sm"
          >
            <Share2 size={16} />
            शेयर
          </button>
        </div>

        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          content={{
            title: `आज का ${shloka.type === 'shloka' ? 'श्लोक' : 'सुविचार'}`,
            text: `${shloka.sanskritText}\n\n${shloka.hindiMeaning}\n\n— ${shloka.source}\n\nगोलोक दिव्य नाम कोष`,
            imageRef: downloadRef,
            fileName: `suvichar-${new Date().toISOString().split('T')[0]}.png`,
          }}
        />
      </div>
    </div>
  );
};

// Full Page View Component
export const ShlokaOfTheDayPage: React.FC = () => {
  return (
    <div className="max-w-2xl mx-auto py-4 px-4 animate-fade-in">
      <div className="text-center mb-4">
        <h1 className="text-2xl font-serif font-bold text-templeRed dark:text-saffron">Daily Divine Wisdom</h1>
        <p className="text-gray-500 dark:text-stone-400 text-sm italic">श्रीमद् भगवद्गीता से प्रतिदिन एक श्लोक</p>
      </div>
      <ShlokaCard />
    </div>
  );
};

// Modal Component for Daily Pop-up
interface ShlokaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShlokaModal: React.FC<ShlokaModalProps> = ({ isOpen, onClose }) => {
  const [shloka, setShloka] = useState<DailyShloka | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setShloka(getShlokaOfTheDay());
      // Preload logo
      const img = new Image();
      img.onload = () => setLogoLoaded(true);
      img.src = GOLOK_LOGO_URL;
    } else {
      setLogoLoaded(false);
    }
  }, [isOpen]);

  const handleDownload = async () => {
    if (!downloadRef.current) return;
    
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(downloadRef.current, {
        backgroundColor: '#fffbeb',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = `suvichar-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen || !shloka) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-gradient-to-br from-amber-50 to-orange-50 dark:from-stone-900 dark:to-stone-800 rounded-3xl shadow-2xl animate-slide-up">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/80 dark:bg-stone-800/80 rounded-full shadow-md hover:scale-110 transition-transform"
        >
          <X size={18} className="text-gray-600 dark:text-stone-400" />
        </button>

        {/* Downloadable Content Area */}
        <div ref={downloadRef} className="bg-gradient-to-br from-amber-50 to-orange-50">
          {/* Header with custom color #FDF5E6 and logo */}
          <div className="px-6 py-5 text-center border-b-2 border-saffron/30" style={{ backgroundColor: '#FDF5E6' }}>
            <div className="relative mx-auto h-16 w-32 mb-2">
              {!logoLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-saffron border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              <img 
                src={GOLOK_LOGO_URL} 
                alt="Golok Logo" 
                className={`mx-auto h-16 w-auto object-contain transition-opacity duration-300 ${logoLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setLogoLoaded(true)}
              />
            </div>
            <h2 className="text-templeRed font-bold text-xl font-hindi">
              आज का {shloka.type === 'shloka' ? 'श्लोक' : 'सुविचार'}
            </h2>
            <p className="text-templeRed/70 text-sm mt-1">{shloka.source}</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Sanskrit */}
            <div className="bg-white rounded-2xl p-4 border border-amber-100">
              <p className="font-hindi text-lg text-center leading-loose text-templeRed whitespace-pre-line">
                {shloka.sanskritText}
              </p>
            </div>

            {/* Hindi Meaning */}
            <div className="bg-amber-50/50 rounded-xl p-4">
              <p className="font-hindi text-sm text-gray-700 leading-relaxed">
                {shloka.hindiMeaning}
              </p>
            </div>

            {/* Tags */}
            {shloka.tags && (
              <div className="flex flex-wrap justify-center gap-2">
                {shloka.tags.slice(0, 3).map(tag => (
                  <span 
                    key={tag}
                    className="px-3 py-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Outside downloadable area */}
        <div className="px-6 pb-6 space-y-3">
          {/* Download & Share Buttons */}
          <div className="flex gap-2">
            <button 
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex-1 py-3 bg-white border-2 border-templeRed/30 text-templeRed font-bold rounded-2xl shadow-md hover:bg-templeRed/5 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download size={18} />
              {isDownloading ? 'Downloading...' : 'Download'}
            </button>
            <button 
              onClick={() => setShowShare(true)}
              data-testid="shloka-modal-share-btn"
              className="flex-1 py-3 bg-gradient-to-r from-saffron to-orange-500 text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <Share2 size={18} />
              शेयर करें
            </button>
          </div>
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="w-full py-3 bg-templeRed text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            जय श्री राम
          </button>
        </div>

        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          content={{
            title: `आज का ${shloka.type === 'shloka' ? 'श्लोक' : 'सुविचार'}`,
            text: `${shloka.sanskritText}\n\n${shloka.hindiMeaning}\n\n— ${shloka.source}\n\nगोलोक दिव्य नाम कोष`,
            imageRef: downloadRef,
            fileName: `suvichar-${new Date().toISOString().split('T')[0]}.png`,
          }}
        />
      </div>
    </div>
  );
};
